import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button
} from '@mui/material';
import {
  CheckCircle as CompletedIcon,
  AccessTime as InProgressIcon,
  Info as InfoIcon,
  EmojiObjects as GoalIcon,
  Psychology as PersonaIcon,
  ArrowForward as ArrowForwardIcon,
  HowToReg as SignupIcon
} from '@mui/icons-material';
import { formatDistanceToNow, format } from 'date-fns';

/**
 * OnboardingStatusCard Component
 *
 * Displays onboarding journey information for a client including:
 * - Completion status and progress
 * - Stage history with durations
 * - Identified goals and challenges
 * - Assigned persona
 *
 * Props:
 * - onboarding: Onboarding object from API
 * - loading: Boolean indicating if data is loading
 * - onViewAssessments: Callback function to navigate to assessments tab
 */
function OnboardingStatusCard({ onboarding, loading = false, onViewAssessments }) {
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Onboarding Journey
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Loading onboarding data...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!onboarding || onboarding.status === 'not_started' || onboarding.status === 'error') {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <InfoIcon color="disabled" />
            <Typography variant="h6" fontWeight="bold">
              Onboarding Journey
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
            {onboarding?.status === 'error'
              ? 'Error loading onboarding data'
              : 'Client has not started onboarding yet'}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const {
    status,
    completedAt,
    totalDurationMinutes,
    stages = [],
    identifiedGoals = [],
    identifiedChallenges = [],
    primaryFocus,
    assignedPersona,
    signupInfo,
    flowName
  } = onboarding;

  const formatSignupMode = (mode) => {
    const labels = {
      self_registered: 'Self-registered',
      individual_invite: 'Individual Invite',
      bulk_invite: 'Bulk Invite',
      general_link: 'General Link',
      admin_created: 'Admin Created'
    };
    return labels[mode] || mode || 'Unknown';
  };

  const isCompleted = status === 'completed';
  const stagesCompleted = stages.filter(s => s.completedAt).length;
  const totalStages = stages.length;
  const progress = totalStages > 0 ? (stagesCompleted / totalStages) * 100 : 0;

  // Get status icon and color
  const getStatusIcon = () => {
    if (isCompleted) return <CompletedIcon sx={{ color: '#2e7d32' }} />;
    return <InProgressIcon sx={{ color: '#ed6c02' }} />;
  };

  const getStatusColor = () => {
    if (isCompleted) return 'success';
    return 'warning';
  };

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            {getStatusIcon()}
            <Typography variant="h6" fontWeight="bold">
              Onboarding Journey
            </Typography>
          </Box>
          <Chip
            label={isCompleted ? 'Completed' : 'In Progress'}
            color={getStatusColor()}
            size="small"
          />
        </Box>

        {/* Signup Source & Flow Name */}
        {(signupInfo || flowName) && (
          <Box mb={2} p={1.5} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Box display="flex" alignItems="center" gap={0.5} mb={1}>
              <SignupIcon sx={{ fontSize: '1rem', color: '#546e7a' }} />
              <Typography variant="subtitle2" fontWeight="bold">
                How They Found Us
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
              {signupInfo?.signupMode && (
                <Chip
                  label={formatSignupMode(signupInfo.signupMode)}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              )}
              {signupInfo?.invitation?.tag && (
                <Chip
                  label={signupInfo.invitation.tag}
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              )}
              {signupInfo?.invitation?.serviceGroup && (
                <Chip
                  label={signupInfo.invitation.serviceGroup}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              )}
            </Box>
            {signupInfo?.invitation?.createdBy && (
              <Typography variant="caption" color="text.secondary">
                Invited by {signupInfo.invitation.createdBy}
              </Typography>
            )}
            {flowName && (
              <Typography variant="caption" color="text.secondary" display="block">
                Onboarding Flow: {flowName}
              </Typography>
            )}
          </Box>
        )}

        {/* Progress Bar */}
        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" color="text.secondary">
              Progress
            </Typography>
            <Typography variant="caption" fontWeight="bold">
              {stagesCompleted} / {totalStages} stages
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 1,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: isCompleted ? '#2e7d32' : '#ed6c02'
              }
            }}
          />
        </Box>

        {/* Completion Info */}
        {isCompleted && completedAt && (
          <Box mb={2}>
            <Typography variant="caption" color="text.secondary" display="block">
              Completed {formatDistanceToNow(new Date(completedAt), { addSuffix: true })}
            </Typography>
            {totalDurationMinutes > 0 && (
              <Typography variant="caption" color="text.secondary">
                Total duration: {Math.round(totalDurationMinutes)} minutes
              </Typography>
            )}
          </Box>
        )}

        {/* Stages */}
        {stages.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>
              Stages
            </Typography>
            <List dense disablePadding>
              {stages.map((stage, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <Divider />}
                  <ListItem disableGutters sx={{ py: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      {stage.completedAt ? (
                        <CompletedIcon sx={{ fontSize: '1rem', color: '#2e7d32', mr: 1 }} />
                      ) : (
                        <InProgressIcon sx={{ fontSize: '1rem', color: '#ed6c02', mr: 1 }} />
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontSize="0.875rem">
                          {stage.stageName}
                        </Typography>
                        {stage.durationMinutes && (
                          <Typography variant="caption" color="text.secondary">
                            {stage.durationMinutes} min
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}

        {/* Identified Goals */}
        {identifiedGoals.length > 0 && (
          <Box mb={2}>
            <Box display="flex" alignItems="center" gap={0.5} mb={1}>
              <GoalIcon sx={{ fontSize: '1rem', color: '#1976d2' }} />
              <Typography variant="subtitle2" fontWeight="bold">
                Identified Goals
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {identifiedGoals.slice(0, 3).map((goal, index) => (
                <Chip
                  key={index}
                  label={goal}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              ))}
              {identifiedGoals.length > 3 && (
                <Chip
                  label={`+${identifiedGoals.length - 3} more`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
                />
              )}
            </Box>
          </Box>
        )}

        {/* Primary Focus */}
        {primaryFocus && (
          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>
              Primary Focus
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {primaryFocus}
            </Typography>
          </Box>
        )}

        {/* Assigned Persona */}
        {assignedPersona && (
          <Box mb={2}>
            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
              <PersonaIcon sx={{ fontSize: '1rem', color: '#9c27b0' }} />
              <Typography variant="subtitle2" fontWeight="bold">
                Assigned Persona
              </Typography>
            </Box>
            <Chip
              label={assignedPersona.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              color="secondary"
              size="small"
              variant="outlined"
            />
          </Box>
        )}

        {/* View Assessments Button */}
        {onViewAssessments && (
          <Box mt={2}>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              endIcon={<ArrowForwardIcon />}
              onClick={onViewAssessments}
              sx={{ textTransform: 'none' }}
            >
              View Onboarding Assessments
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default OnboardingStatusCard;
